
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Posts' },
    { id: 'ai-insights', name: 'AI Insights' },
    { id: 'business-development', name: 'Business Development' },
    { id: 'marketing', name: 'Marketing' },
    { id: 'case-studies', name: 'Case Studies' }
  ];

  const blogPosts = [
    {
      id: 1,
      title: 'The Future of AI in Small Business Marketing',
      excerpt: 'Discover how artificial intelligence is revolutionizing marketing strategies for small businesses and driving unprecedented growth.',
      category: 'ai-insights',
      author: 'Sarah Johnson',
      date: '2024-01-15',
      readTime: '5 min read',
      image: 'https://public.readdy.ai/ai/img_res/c35ca8b70cd8096ff854762f4809ecd2.jpg',
      tags: ['AI Marketing', 'Small Business', 'Growth']
    },
    {
      id: 2,
      title: 'ROI Measurement: Tracking Your AI Investment Success',
      excerpt: 'Learn proven methodologies for measuring and optimizing the return on investment from your AI marketing initiatives.',
      category: 'business-development',
      author: 'Michael Chen',
      date: '2024-01-12',
      readTime: '7 min read',
      image: 'https://readdy.ai/api/search-image?query=business%20analytics%20dashboard%20showing%20ROI%20metrics%20and%20performance%20charts%2C%20professional%20workspace%20with%20multiple%20monitors%20displaying%20data%20visualization&width=400&height=250&seq=blog2&orientation=landscape',
      tags: ['ROI', 'Analytics', 'Performance']
    },
    {
      id: 3,
      title: 'Conversational AI: Beyond Basic Chatbots',
      excerpt: 'Explore advanced conversational AI strategies that create meaningful customer interactions and drive business results.',
      category: 'ai-insights',
      author: 'Emily Rodriguez',
      date: '2024-01-10',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=sophisticated%20AI%20chatbot%20interface%20with%20natural%20conversation%20flow%2C%20modern%20UI%20design%2C%20customer%20service%20representative%20using%20advanced%20technology&width=400&height=250&seq=blog3&orientation=landscape',
      tags: ['Conversational AI', 'Customer Service', 'Technology']
    },
    {
      id: 4,
      title: 'Case Study: 300% Lead Increase with AI Automation',
      excerpt: 'Real-world success story showing how one client achieved remarkable lead generation growth through strategic AI implementation.',
      category: 'case-studies',
      author: 'David Park',
      date: '2024-01-08',
      readTime: '8 min read',
      image: 'https://readdy.ai/api/search-image?query=successful%20business%20team%20celebrating%20growth%20results%20with%20upward%20trending%20charts%20and%20graphs%2C%20professional%20office%20setting%2C%20achievement%20atmosphere&width=400&height=250&seq=blog4&orientation=landscape',
      tags: ['Case Study', 'Lead Generation', 'Success Story']
    },
    {
      id: 5,
      title: 'Local SEO Optimization for AI-Powered Businesses',
      excerpt: 'Master local search optimization strategies specifically designed for businesses leveraging AI technologies.',
      category: 'marketing',
      author: 'Lisa Thompson',
      date: '2024-01-05',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=local%20business%20map%20with%20SEO%20optimization%20elements%2C%20location%20pins%20and%20search%20results%2C%20digital%20marketing%20concept%20with%20geographic%20targeting&width=400&height=250&seq=blog5&orientation=landscape',
      tags: ['Local SEO', 'Digital Marketing', 'Optimization']
    },
    {
      id: 6,
      title: 'Building Trust Through AI Transparency',
      excerpt: 'Learn how to build customer trust by implementing transparent AI practices and clear communication strategies.',
      category: 'business-development',
      author: 'Robert Kim',
      date: '2024-01-03',
      readTime: '5 min read',
      image: 'https://readdy.ai/api/search-image?query=transparent%20business%20communication%20with%20AI%20technology%2C%20trust%20building%20elements%2C%20professional%20handshake%20with%20digital%20overlay%2C%20modern%20corporate%20environment&width=400&height=250&seq=blog6&orientation=landscape',
      tags: ['Trust Building', 'Transparency', 'Customer Relations']
    }
  ];

  const filteredPosts = selectedCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              AI Marketing Insights & Business Growth
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Stay ahead with expert insights on AI marketing, business development strategies, and proven growth tactics for modern businesses.
            </p>
            
            {/* Newsletter Subscription */}
            <div className="max-w-md mx-auto">
              <form 
                action="https://readdy.ai/api/form/d44s6qn0vhksjg8rfn20" 
                method="POST" 
                data-readdy-form
                className="flex gap-2"
              >
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  required
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Subscribe
                </button>
              </form>
              <p className="text-sm text-gray-500 mt-2">Get weekly AI marketing insights delivered to your inbox</p>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article key={post.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      {categories.find(cat => cat.id === post.category)?.name}
                    </span>
                    <span className="text-xs text-gray-500">{post.readTime}</span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                    {post.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex flex-wrap gap-1 mb-4">
                    {post.tags.map((tag, index) => (
                      <span key={index} className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-bold">
                          {post.author.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{post.author}</p>
                        <p className="text-xs text-gray-500">{post.date}</p>
                      </div>
                    </div>
                    
                    <button className="text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer whitespace-nowrap">
                      Read More →
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
